// Angular Core Imports
import { CommonModule } from '@angular/common';
import { Component, HostListener, ViewContainerRef, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

// Application Services Imports
import { OnlineStatusService } from './core/services/online-status.service';

// Application Components Imports
import { LoaderComponentComponent } from './layouts/loader-component/loader-component.component';
import { ConfirmationDialogComponent } from './shared/confirmation-dialog/confirmation-dialog.component';
import { OrgAppInfoHelperService } from './core/services/org-app-info-helper.service';
import { AuthService } from './core/services/auth.service';
import { environment } from '../environments/environment';
import { L10n, setCulture } from "@syncfusion/ej2-base";
import { TabSdk } from '@techextensor/tab-sdk';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [RouterOutlet, LoaderComponentComponent, CommonModule, ConfirmationDialogComponent]
})
export class AppComponent {
  private globalZCounter = 0;
  private container: ViewContainerRef = inject(ViewContainerRef);
  public onlineStatus: OnlineStatusService = inject(OnlineStatusService);
  private readonly orgAppInfoHelper: OrgAppInfoHelperService = inject(OrgAppInfoHelperService);
  private readonly authService: AuthService = inject(AuthService);

  /**
   * Constructor for the AppComponent.
   * Registers the form designer and tab child screen components with the injector.
   * Sets up window events for opening the sidebar and modal.
   */
  constructor() {
    // Set up window events for opening the sidebar and modal
    this.windowEvents();
    if (this.authService.accessToken?.length || window.location?.pathname?.includes('documents')) {
      const orgInfo = TabSdk.store.getOrgInfo();
      if (orgInfo) this.orgAppInfoHelper.processOrgInfo(orgInfo ?? {});
    } else {
      TabSdk.store.clearSession();
    }

    if (!TabSdk.store.environmentId) {
      TabSdk.store.environmentName = environment.name;
    }
    this.processMultiLanguage();
  }

  // Set up window events for opening the sidebar and modal
  private windowEvents() {
    window.addEventListener('openTabSidebar', (event: any) => {
      this.createDynamicComponent('SidebarComponent', event.detail);
    });

    window.addEventListener('openTabModal', (event: any) => {
      this.createDynamicComponent('DialogComponent', event.detail);
    });
  }

  /**
   * Dynamically loads and creates a component.
   * @param {string} componentName - The name of the component to be loaded.
   * @param {any} data - Data to pass to the component.
   */
  private async createDynamicComponent(componentName: string, data: any) {
    let componentModule: any;

    // Lazy load the required component based on its name
    switch (componentName) {
      case 'SidebarComponent':
        componentModule = await import('./shared/sidebar/sidebar.component');
        break;

      case 'DialogComponent':
        componentModule = await import('./shared/dialog/dialog.component');
        break;

      default:
        console.error('Component not found for lazy loading');
        return;
    }

    // Create the dynamic component using the ViewContainerRef
    const componentRef: any = this.container.createComponent(componentModule[componentName]);
    componentRef.instance.data = {
      ...data,
      dynamicZ: 10001 + (++this.globalZCounter * 0.005)
    };

    // Inert the previous overlay, or the main content wrapper if this is the first overlay
    const nativeEl = componentRef.location.nativeElement as HTMLElement;
    const prevSibling = nativeEl.previousElementSibling;
    if (prevSibling?.tagName === 'APP-ROOT') {
      document.getElementById('app-content')?.setAttribute('inert', '');
    } else {
      prevSibling?.setAttribute('inert', '');
    }

    componentRef?.instance?.destroyInstance?.subscribe((screenId: string) => {
      if (screenId === data.screenId) {
        componentRef?.destroy();
        if (nativeEl?.isConnected) nativeEl.remove();
        this.globalZCounter--;
        this.restoreLastActive();
      }
    });
  }

  /**
   * Toggles the fullscreen state of the target element
   * @param {CustomEvent} event - The custom event containing the targetId of the element to toggle fullscreen
   * */
  @HostListener('window:tabExpandToggle', ['$event'])
  onTabExpandToggle(event: any) {
    const { targetId } = event.detail;
    TabSdk.util.tabExpandableToggleFullScreen(targetId);
  }

  /**
   * Closes the sidebar when the escape key is pressed.
   */
  @HostListener('document:keydown.escape')
  onEscPress() {
    this.handleSidebarClose();
    this.handleCollapse();
  }

  /**
   * Closes the sidebar by removing the last app-sidebar element from the DOM.
   */
  private handleSidebarClose() {
    // Get all app-sidebar elements
    const elements = Array.from(document.querySelectorAll('app-sidebar')) as HTMLElement[];
    // Get the last element
    const lastElement = elements.pop();
    // If the last element exists, remove it
    if (lastElement) {
      lastElement.remove();
      this.globalZCounter--;
      this.restoreLastActive();
    }
  }

  /**
   * Removes inert from the now-topmost overlay, or from app-root if no overlays remain.
   */
  private restoreLastActive() {
    const lastOverlay = document.querySelectorAll('app-sidebar, app-dialog');
    if (lastOverlay.length > 0) {
      lastOverlay[lastOverlay.length - 1].removeAttribute('inert');
    } else {
      document.getElementById('app-content')?.removeAttribute('inert');
    }
  }

  /**
   * Handles the collapse of the tab content when the escape key is pressed.
   * Finds the compress button and simulates a click event on it.
   */
  private handleCollapse() {
    // Get the compress button element
    const compressButton = document.querySelector('.tab-expandable-btn .fa-compress');
    if (compressButton) {
      // Get the button element
      const buttonElement = compressButton.closest('.tab-expandable-btn') as HTMLButtonElement;
      if (buttonElement) {
        // Simulate a click event on the button
        buttonElement.click();

        // Remove focus from the button after simulating the click event
        buttonElement.blur();
      }
    }
  }

  /**
   * Process the translations for the Syncfusion components.
   * @param {string} lang - The language code.
   * @param {any} translations - The translations JSON object.
   */
  private processMultiLanguage() {
    TabSdk.translator.registerIntegration((lang, translations) => {
      if (L10n && typeof L10n.load === 'function') {
        L10n.load({
          [lang]: {
            'grid': translations,
            'pager': translations,
            'querybuilder': translations
          }
        });
        setCulture(lang);
      }
    });
  }
}
